import { motion } from 'framer-motion'
import { faHandWave } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../../components/icon/Icon'
import ActivityFeed from '../../components/activity-feed/ActivityFeed'
import AppLayout from '../../components/app-layout/AppLayout'
import AchievementsGrid from '../../components/achievements-grid/AchievementsGrid'
import DailyPepTalk from '../../components/daily-pep-talk/DailyPepTalk'
import DashboardInsights from '../../components/dashboard-insights/DashboardInsights'
import MoveInCountdown from '../../components/move-in-countdown/MoveInCountdown'
import NotificationsTeaser from '../../components/notifications-teaser/NotificationsTeaser'
import RecentlyViewedDiscover from '../../components/recently-viewed-discover/RecentlyViewedDiscover'
import SavedListingsPreview from '../../components/saved-listings-preview/SavedListingsPreview'
import SavedSearchesSummary from '../../components/saved-searches-summary/SavedSearchesSummary'
import SmartInsights from '../../components/smart-insights/SmartInsights'
import StreakPill from '../../components/streak-pill/StreakPill'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { isEmbeddedExperience } from '../../_utilities/embed-context'
import {
  PageStack,
  HeroBlock,
  Greeting,
  GreetingSubtitle,
  QuickActionsGrid,
  QuickActionCard,
  QuickActionEmoji,
  QuickActionTitle,
  QuickActionSubtitle,
  SectionHeader,
  SectionTitle,
  SectionLink,
  PreferencesGrid,
  PreferenceCell,
  PreferenceLabel,
  PreferenceValue,
} from './styled'
import {
  QUICK_ACTIONS,
  dashboardContainerMotion,
  dashboardItemMotion,
  getTimeOfDayGreeting,
  formatPriceRange,
  formatBedroomCounts,
  formatPropertyTypes,
  formatDwellingScope,
  formatCityList,
  getDisplayFirstName,
} from './helpers'

/**
 * Real dashboard surface for signed-in renters. Shows a personalised
 * greeting, quick-action cards into the sub-pages, a preferences digest,
 * and a saved-listings preview (empty state until Slice 5 hooks up
 * cross-site saving).
 *
 * @returns {JSX.Element}
 */
const DashboardPage = (): JSX.Element => {
  const { profile } = useQuickPassProfile()

  const greetingPrefix = getTimeOfDayGreeting()
  const firstName = getDisplayFirstName(profile)
  const preferences = profile?.unitPreferences ?? null

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack
        variants={dashboardContainerMotion}
        initial="hidden"
        animate="visible"
      >
        <HeroBlock variants={dashboardItemMotion}>
          <StreakPill />
          <Greeting>
            {greetingPrefix}, {firstName}{' '}
            <Icon icon={faHandWave} label="Wave" />
          </Greeting>
          <GreetingSubtitle>
            Your QuickPass is ready. Here&apos;s what we know about your search.
          </GreetingSubtitle>
        </HeroBlock>

        <motion.div variants={dashboardItemMotion}>
          <DailyPepTalk />
        </motion.div>

        <motion.div variants={dashboardItemMotion}>
          <NotificationsTeaser />
        </motion.div>

        <motion.div variants={dashboardItemMotion}>
          <MoveInCountdown />
        </motion.div>

        <motion.div variants={dashboardItemMotion}>
          <DashboardInsights />
        </motion.div>

        <motion.div variants={dashboardItemMotion}>
          <SmartInsights />
        </motion.div>

        <QuickActionsGrid variants={dashboardItemMotion}>
          {QUICK_ACTIONS.map(action => (
            <QuickActionCard key={action.to} to={action.to}>
              <QuickActionEmoji aria-hidden="true">
                <Icon icon={action.icon} />
              </QuickActionEmoji>
              <QuickActionTitle>{action.title}</QuickActionTitle>
              <QuickActionSubtitle>{action.subtitle}</QuickActionSubtitle>
            </QuickActionCard>
          ))}
        </QuickActionsGrid>

        {preferences !== null ? (
          <motion.div variants={dashboardItemMotion}>
            <SectionHeader>
              <SectionTitle>What you&apos;re looking for</SectionTitle>
              <SectionLink to="/profile">Edit</SectionLink>
            </SectionHeader>
            <PreferencesGrid>
              <PreferenceCell>
                <PreferenceLabel>Cities</PreferenceLabel>
                <PreferenceValue>
                  {formatCityList(preferences.cities)}
                </PreferenceValue>
              </PreferenceCell>
              <PreferenceCell>
                <PreferenceLabel>Budget</PreferenceLabel>
                <PreferenceValue>
                  {formatPriceRange(preferences.priceMin, preferences.priceMax)}
                </PreferenceValue>
              </PreferenceCell>
              <PreferenceCell>
                <PreferenceLabel>Bedrooms</PreferenceLabel>
                <PreferenceValue>
                  {formatBedroomCounts(preferences.bedroomCounts)}
                </PreferenceValue>
              </PreferenceCell>
              <PreferenceCell>
                <PreferenceLabel>Place type</PreferenceLabel>
                <PreferenceValue>
                  {formatPropertyTypes(preferences.propertyTypes)}
                </PreferenceValue>
              </PreferenceCell>
              <PreferenceCell>
                <PreferenceLabel>Whole or unit</PreferenceLabel>
                <PreferenceValue>
                  {formatDwellingScope(preferences.dwellingScope)}
                </PreferenceValue>
              </PreferenceCell>
            </PreferencesGrid>
          </motion.div>
        ) : null}

        <motion.div variants={dashboardItemMotion}>
          <SavedListingsPreview />
        </motion.div>

        {isEmbeddedExperience() === false ? (
          <motion.div variants={dashboardItemMotion}>
            <RecentlyViewedDiscover />
          </motion.div>
        ) : null}

        <motion.div variants={dashboardItemMotion}>
          <SavedSearchesSummary />
        </motion.div>

        <motion.div variants={dashboardItemMotion}>
          <ActivityFeed />
        </motion.div>

        <motion.div variants={dashboardItemMotion}>
          <AchievementsGrid />
        </motion.div>
      </PageStack>
    </AppLayout>
  )
}

export default DashboardPage
